import { apiFetch } from './http';
import { retryAfterSeconds } from './retryAfter';
import { apiBaseUrl } from './baseUrl';
import { clubSession } from '../auth/instance';

// ─────────────────────────────────────────────────────────────────────
// RE-PROVING PRESENCE WITHOUT SIGNING OUT (#750 M2b-1).
//
// The API refuses organization authority twelve hours after the last
// authentication. `/me/step-up/*` is how somebody passes that refusal: complete
// a ceremony, and the session's bearer ROTATES with a fresh stamp.
//
// ── THE CALLER MUST SWAP ITS BEARER, AND THAT IS THIS MODULE'S JOB ──
//
// The rotation REVOKES the old token. A client that ignores the new one is
// signed out at its next request — so every success path here hands the token
// straight to `clubSession.rotated()` before returning. Nothing else in the app
// should ever see these tokens.
//
// ── COOKIE AND BEARER BOTH MOVE, AND THEY MOVE DIFFERENTLY ─────────
//
// The server answers `Set-Cookie` AND a token, because both transports are
// live: the browser portal authenticates by cookie after a reload, the native
// WebView shares the same jar (`sharedCookiesEnabled`), and an in-memory bearer
// takes precedence server-side wherever it exists.
//
// The cookie is the browser's to store — `credentials: 'include'` is already on
// every request, so it lands without us touching it. The BEARER is the half
// that needs code, and the half that breaks loudly if it is missed: the stale
// in-memory token would win over the fresh cookie on the very next request and
// answer 401.
//
// ── WHAT THIS MODULE DOES NOT DECIDE ───────────────────────────────
//
// WHEN to prompt. That is the prompt's business, driven by
// `reauthenticationSignal`. These are four requests and a token swap.
// ─────────────────────────────────────────────────────────────────────

/** What the API answers on a completed step-up. */
export interface StepUpResult {
  token: string;
  expiresAt: string;
  authenticatedAt: string;
}

export type StepUpOutcome =
  | { ok: true; authenticatedAt: string }
  /**
   * A machine code from the API, never a sentence. The screen decides the
   * words — CLAUDE.md: API error CODES are not translated.
   *
   * `session_changed` is this module's own, not the server's: the ceremony
   * completed but the session it was started under is gone. See `take`.
   */
  | { ok: false; code: string };

/**
 * What asking for a code answers. ITS OWN TYPE (#772 review).
 *
 * It used to share `StepUpOutcome` and report `authenticatedAt: ''`, with a
 * comment claiming that prevented «requested» being read as «fresh». The
 * comment did; the type did not — any prompt checking `ok` would have closed
 * itself the moment the mail was sent, before anybody proved anything. There
 * is no `authenticatedAt` here to misread.
 */
export type StepUpRequestOutcome =
  { ok: true } | { ok: false; code: string; retryAfterSeconds?: number };

/** Options for the browser's `navigator.credentials.get()`. */
export interface StepUpAssertionOptions {
  challenge: string;
  rpId: string;
  timeout: number;
  userVerification: 'required';
  allowCredentials: { id: string; transports: string[] }[];
}

const failure = async (
  res: Response,
): Promise<{ ok: false; code: string; retryAfterSeconds?: number }> => {
  const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
  // `unknown_error` rather than the status: a caller that switches on codes
  // should not have to also switch on numbers, and the API answers a code for
  // every refusal it means.
  const code = typeof body?.error === 'string' ? body.error : 'unknown_error';
  // A SPENT BUDGET CARRIES ITS WAIT (#778 Q7): the mail budget on the code
  // door, the mint budget on the passkey one. 0 when it could not be read.
  return code === 'too_many_attempts'
    ? { ok: false, code, retryAfterSeconds: retryAfterSeconds(res) }
    : { ok: false, code };
};

/**
 * Hand a completed step-up's token to the session.
 *
 * THE ONLY PLACE THAT READS `token` OFF THESE RESPONSES, so «did we remember
 * to swap the bearer?» has one answer rather than four.
 */
async function take(result: StepUpResult, incarnation: number): Promise<StepUpOutcome> {
  // ── A LATE SUCCESS MAY NOT LAND ON A DIFFERENT SIGN-IN (#772) ────
  //
  // The ceremony's answer can arrive after the session that started it is
  // gone — signed out, reloaded, replaced by somebody else signing in on the
  // same tab. `rotated` refuses an incarnation that no longer matches, and the
  // refusal has to reach the caller as a FAILURE: reporting `ok: true` would
  // tell a prompt that the current session is fresh when the proof belonged to
  // a different one.
  //
  // The token is simply dropped. It is a live credential for a session nobody
  // is using, and it expires on the server's schedule; the alternative —
  // keeping it somewhere until it might be wanted — is a bearer in storage,
  // which is the thing this codebase does not do.
  if (!clubSession.rotated({ token: result.token, expiresAt: result.expiresAt, incarnation })) {
    // ── THE COOKIE IS ALREADY INSTALLED BY NOW (#773) ──────────────
    //
    // Refusing the bearer is not enough, and the comment above used to say
    // the token was «simply dropped». It was dropped from JavaScript — the
    // BROWSER had already applied the `Set-Cookie` that came with this same
    // response, before any of this code ran, and no guard in JavaScript can
    // get in front of that.
    //
    // So the browser's durable club credential is now this token, which
    // belongs to a session nobody is using. Concretely:
    //
    //   A starts a step-up        this tab holds A's bearer, request in flight
    //   A signs out               the rotated session survives the sign-out,
    //                             because the logout presented the token the
    //                             rotation had already revoked
    //   B signs in                the cookie becomes B's
    //   A's answer arrives        the cookie becomes A's rotated token — LIVE,
    //                             fresh, and nobody's
    //   someone reloads           `restore()` asks `/me` with that cookie and
    //                             is answered «A». B's screen comes back as A.
    //
    // That is a silent actor substitution, which is the one failure mode a
    // session layer may not have. It is reachable only through this ordering,
    // and only here, because this is the one response that installs a cookie
    // for a session the client has decided not to use.
    //
    // ── WHERE THE FIX ACTUALLY LIVES (#782 review) ─────────────────
    //
    // NOT HERE. The ordering above is made safe on the server: a logout
    // revokes the whole rotation chain, so by the time this late answer
    // exists, the credential it carries is already dead and the cookie it
    // installs authenticates nobody. That holds with no further request from
    // this browser — which is the point, because a browser that cannot reach
    // the network is exactly the case a client-side repair cannot cover.
    //
    // What this call adds is tidying with real value: it CLEARS the stale
    // cookie, so the browser stops presenting something it will keep being
    // refused for, and it revokes the descendant in orderings where no
    // sign-out happened at all. Awaited so the caller is never told
    // «session_changed» while the tidying is still in flight.
    await discardSupersededCredential(result.token);
    return { ok: false, code: 'session_changed' };
  }
  return { ok: true, authenticatedAt: result.authenticatedAt };
}

/**
 * Revoke a rotated credential this client has decided not to adopt, and clear
 * the cookie the response installed for it (#773).
 *
 * ── A RAW FETCH, NOT `apiFetch`, AND THAT IS THE POINT ─────────────
 *
 * `apiFetch` attaches whatever credential the CURRENT session holds, which is
 * exactly the one that must not be revoked — it belongs to whoever is signed
 * in now. This request has to present the superseded token and nothing else.
 *
 * `credentials: 'include'` so the browser applies the clearing `Set-Cookie`;
 * without it the row would be revoked and the dead cookie left in place, which
 * fixes the security problem and leaves a browser that keeps presenting a
 * credential it will keep being refused for.
 *
 * ── IT DOES NOT CARRY THE SECURITY PROPERTY, AND CANNOT (#782 review) ──
 *
 * An earlier version of this comment claimed that a failed repair still left a
 * revoked row. That does not follow: if the request never reaches the server
 * nothing is revoked, and the browser has already applied the cookie by then —
 * so a reload could still come back as the other account. A client-side
 * best-effort request cannot establish «never» about anything.
 *
 * The guarantee is established on the SERVER instead, by a request that has
 * definitely arrived and arrived EARLIER: `revokeSession` revokes the whole
 * rotation chain, so the sign-out that precedes the late answer has already
 * killed the credential this response carries. See `apps/api/src/auth/sessions.ts`.
 *
 * What this is still worth: it CLEARS THE COOKIE, so the browser stops
 * presenting a credential it will keep being refused for, and it revokes the
 * descendant in the orderings where no sign-out happened at all — a late answer
 * after a plain reload, say, where nothing else would. Defence in depth behind a
 * property that holds without it.
 *
 * NEVER THROWS, for the same reason it is best-effort: the caller is already
 * reporting a failure and must not depend on this.
 */
async function discardSupersededCredential(token: string): Promise<void> {
  const base = apiBaseUrl();
  if (!base) return;
  try {
    await fetch(base + '/auth/club/logout', {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    // The row may still be live. Nothing better is available from here, and
    // the token is not retained anywhere to try again with.
  }
}

// ── THE E-MAIL DOOR ────────────────────────────────────────────────

/**
 * Ask for a code. Always resolves for an authenticated caller whose account
 * has a LOCAL identity; 202 carries no information about anything.
 *
 * The code goes to the identity's own proven address — not to anything the
 * caller names, and not to `User.email`. There is nothing to pass here.
 */
export async function requestStepUpCode(): Promise<StepUpRequestOutcome> {
  const res = await apiFetch('/me/step-up/email/request', { method: 'POST' });
  if (!res.ok) return failure(res);
  // NOT a completed step-up — nothing has been proved yet, so no token, no
  // rotation, and NO `authenticatedAt` to be mistaken for one.
  return { ok: true };
}

/** Spend the code. On success the bearer has already been swapped. */
export async function verifyStepUpCode(code: string): Promise<StepUpOutcome> {
  // CAPTURED BEFORE THE REQUEST THAT ROTATES. The window that matters is
  // between issuing this call and handling its answer — that is where a
  // sign-out and a different sign-in can fit.
  const startedUnder = clubSession.incarnation();
  const res = await apiFetch('/me/step-up/email', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) return failure(res);
  return await take((await res.json()) as StepUpResult, startedUnder);
}

// ── THE PASSKEY DOOR ───────────────────────────────────────────────

/** Open a ceremony. The challenge is minted for the caller's own identity. */
export async function stepUpAssertionOptions(): Promise<
  | { ok: true; options: StepUpAssertionOptions }
  | { ok: false; code: string; retryAfterSeconds?: number }
> {
  const res = await apiFetch('/me/step-up/passkey/options', { method: 'POST' });
  if (!res.ok) return failure(res);
  return { ok: true, options: (await res.json()) as StepUpAssertionOptions };
}

/**
 * Present the assertion. On success the bearer has already been swapped.
 *
 * `assertion` is whatever `navigator.credentials.get()` returned, serialised by
 * the caller. UNTYPED ON PURPOSE: this module does not model WebAuthn, and a
 * partial type here would be a second, wrong description of a shape the
 * browser owns and the server verifies.
 */
export async function verifyStepUpPasskey(assertion: unknown): Promise<StepUpOutcome> {
  const startedUnder = clubSession.incarnation();
  const res = await apiFetch('/me/step-up/passkey', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ response: assertion }),
  });
  if (!res.ok) return failure(res);
  return await take((await res.json()) as StepUpResult, startedUnder);
}

/**
 * Which doors this account can actually use.
 *
 * ASKED RATHER THAN ASSUMED. An account with no passkey gets 409
 * `no_passkey_registered` from the options route, and offering a button that
 * answers that is worse than not offering it. The e-mail door is available to
 * every account with a LOCAL identity, which is every account that can step up
 * at all — so there is nothing to ask about it.
 */
export async function stepUpDoors(): Promise<{ passkey: boolean }> {
  const res = await apiFetch('/me/passkeys');
  if (!res.ok) return { passkey: false };
  const body = (await res.json().catch(() => null)) as { passkeys?: unknown } | null;
  return { passkey: Array.isArray(body?.passkeys) && body.passkeys.length > 0 };
}
